import { useState, type ReactElement, type ReactNode } from 'react';
import { Alert, Button, Input, Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { formatDateTime } from '../../shared/format/entity-detail.js';
import { EntityActionMenu, type EntityAction } from './EntityActionMenu.js';
import { EmptyState } from './EmptyState.js';

export interface EntityNote {
  id: string;
  body: string;
  author: string;
  createdAt: string;
  badge?: ReactNode;
  visibleActions?: ReactNode;
  actions?: EntityAction[];
}
export function EntityNotesFeed({
  notes,
  onSave,
  saving = false,
  loading = false,
  error,
  extra,
}: {
  notes: EntityNote[];
  onSave: (body: string) => Promise<unknown>;
  saving?: boolean;
  loading?: boolean;
  error?: Error | null;
  extra?: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [pending, setPending] = useState(false);
  const submit = async (): Promise<void> => {
    if (!draft.trim() || saving || pending) return;
    setPending(true);
    setSaveError(null);
    try {
      await onSave(draft.trim());
      setDraft('');
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause : new Error(t('crm.entity_detail.save_error')));
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="entity-notes-feed">
      <Input.TextArea
        data-testid="entity-note-composer"
        aria-label={t('crm.entity_detail.new_note')}
        placeholder={t('crm.entity_detail.new_note')}
        value={draft}
        maxLength={10_000}
        rows={3}
        disabled={saving || pending}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault();
            void submit();
          }
        }}
      />
      <div className="entity-list-toolbar">
        <Button
          data-testid="entity-note-save"
          type="primary"
          size="small"
          disabled={!draft.trim()}
          loading={saving || pending}
          onClick={() => void submit()}
        >
          {t('crm.entity_detail.add')}
        </Button>
        <span className="entity-meta">{t('crm.entity_detail.note_shortcut')}</span>
        <div>{extra}</div>
      </div>
      {error || saveError ? (
        <Alert
          type="error"
          showIcon
          title={t('crm.entity_detail.save_error')}
          description={(error ?? saveError)?.message}
        />
      ) : null}
      {loading ? (
        <Skeleton active />
      ) : notes.length === 0 ? (
        <EmptyState text={t('crm.entity_detail.empty_notes')} />
      ) : (
        [...notes]
          .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
          .map((note) => (
            <article key={note.id} className="entity-note">
              <div className="entity-list-toolbar">
                <div className="entity-meta">
                  {note.author} ·{' '}
                  <time dateTime={note.createdAt}>{formatDateTime(note.createdAt)}</time>
                  {note.badge ? <> · {note.badge}</> : null}
                </div>
                <div className="entity-inline-actions">
                  {note.visibleActions}
                  {note.actions?.length ? <EntityActionMenu actions={note.actions} /> : null}
                </div>
              </div>
              <p
                className={
                  expanded.has(note.id)
                    ? 'entity-note-text'
                    : 'entity-note-text entity-note-clamped'
                }
              >
                {note.body}
              </p>
              {note.body.split('\n').length > 6 || note.body.length > 300 ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    setExpanded((previous) => {
                      const next = new Set(previous);
                      if (next.has(note.id)) next.delete(note.id);
                      else next.add(note.id);
                      return next;
                    })
                  }
                >
                  {t(
                    expanded.has(note.id)
                      ? 'crm.entity_detail.show_less'
                      : 'crm.entity_detail.show_more',
                  )}
                </Button>
              ) : null}
            </article>
          ))
      )}
    </div>
  );
}
